import { getSelectedModel } from './config';
import { llmLog } from '../../utils/logger';

// 使用用户选择的模型
const readModel = () => getSelectedModel();

// API 代理端点
// 优先使用 Cloudflare Worker（支持大文件），否则使用 Netlify Functions
const API_BASE = import.meta.env.VITE_LLM_API || import.meta.env.VITE_SYNC_API || '/api';
const API_PROXY_URL = `${API_BASE}/llm`;

// 网络错误事件（供 UI 监听）
type NetworkErrorCallback = (error: string) => void;
let networkErrorCallback: NetworkErrorCallback | null = null;

export function setNetworkErrorCallback(callback: NetworkErrorCallback | null) {
  networkErrorCallback = callback;
}

function notifyNetworkError(error: string) {
  llmLog.warn('network error:', error);
  networkErrorCallback?.(error);
}

// 连接预热 - 提前建立连接
let warmedUp = false;
export function warmupConnection() {
  if (warmedUp || typeof fetch === 'undefined') return;
  warmedUp = true;
  
  // 预热代理端点
  fetch(API_PROXY_URL, { method: 'OPTIONS' }).catch(() => {});
  llmLog.debug('连接预热已启动');
}

type OpenRouterOptions = {
  jsonMode?: boolean;
};

export async function callOpenRouter(prompt: string, opts: OpenRouterOptions = {}): Promise<string | null> {
  const model = readModel();
  if (typeof fetch === 'undefined') return null;

  try {
    const res = await fetch(API_PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model,
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.2,
        ...(opts.jsonMode ? { response_format: { type: 'json_object' } } : {}),
      }),
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => '');
      notifyNetworkError(`请求失败 (${res.status})`);
      llmLog.warn('proxy non-200:', res.status, errText.slice(0, 100));
      return null;
    }

    const data = await res.json();
    if (data.error) {
      notifyNetworkError(data.error);
      return null;
    }
    
    const text = data?.choices?.[0]?.message?.content;
    if (typeof text === 'string' && text.trim()) return text.trim();
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : '网络连接失败';
    notifyNetworkError(message);
    llmLog.warn('call failed:', err);
  }

  return null;
}

// 支持音频的 OpenRouter 调用
// 官方文档: https://openrouter.ai/docs/features/multimodal/audio
export interface OpenRouterAudioParams {
  audioBase64: string;
  mimeType: string;
  prompt: string;
}

export async function callOpenRouterWithAudio(params: OpenRouterAudioParams): Promise<string | null> {
  const model = readModel();
  if (typeof fetch === 'undefined') {
    notifyNetworkError('浏览器不支持网络请求');
    return null;
  }

  // 根据 mimeType 确定格式
  let format: 'wav' | 'mp3' = 'wav';
  if (params.mimeType.includes('mp3') || params.mimeType.includes('mpeg')) {
    format = 'mp3';
  }

  // 计算音频大小（KB）
  const audioSizeKB = Math.round(params.audioBase64.length / 1024);
  llmLog.debug('audio request:', { model, format, sizeKB: audioSizeKB });

  // 根据音频大小动态设置超时（每 100KB 增加 10 秒，最少 60 秒，最多 180 秒）
  const timeoutMs = Math.min(180000, Math.max(60000, 60000 + Math.floor(audioSizeKB / 100) * 15000));

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch(API_PROXY_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model,
        messages: [{
          role: 'user',
          content: [
            { type: 'text', text: params.prompt },
            {
              type: 'input_audio',
              input_audio: {
                data: params.audioBase64,
                format,
              },
            },
          ],
        }],
        temperature: 0.2,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      notifyNetworkError(`音频请求失败 (${res.status})`);
      llmLog.warn('audio non-200:', res.status, detail.slice(0, 200));
      return null;
    }

    const data = await res.json();
    if (data.error) {
      notifyNetworkError(data.error);
      return null;
    }
    
    const text = data?.choices?.[0]?.message?.content;
    if (typeof text === 'string' && text.trim()) {
      llmLog.debug('audio success, length:', text.length);
      return text.trim();
    }
    notifyNetworkError('AI 返回内容为空');
    llmLog.warn('audio: empty response', data);
  } catch (err: unknown) {
    clearTimeout(timeoutId);
    const isAbortError = err instanceof Error && err.name === 'AbortError';
    if (isAbortError) {
      notifyNetworkError(`请求超时 (${Math.round(timeoutMs/1000)}秒)`);
      llmLog.warn('audio timeout after', timeoutMs, 'ms');
    } else {
      const message = err instanceof Error ? err.message : '网络连接失败';
      notifyNetworkError(message);
      llmLog.warn('audio failed:', message);
    }
  }

  return null;
}
